import { ChangeEvent, useMemo, useState } from "react";
import {
  ExamImportRow,
  ExamImportSummary,
  buildTheoreticalExamWhatsAppMessage,
  buildWhatsAppUrl,
  importTheoreticalExamPdf,
} from "../../api/students";
import { ApiError } from "../../api/client";
import { WhatsAppLink } from "../WhatsAppLink";
import { UI_LOADING } from "../../constants/uiLabels";
import "./StudentImportModal.css";

type Props = {
  onClose: () => void;
  onImported: (summary: ExamImportSummary) => void;
};

const MAX_BYTES = 15 * 1024 * 1024;

function formatDateTr(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

function statusLabel(row: ExamImportRow): string {
  switch (row.status) {
    case "new":
      return "Yeni — mesaj gönder";
    case "existing":
    case "updated_only":
      return "Bu dönemde daha önce işlendi";
    case "not_found":
      return "Sistemde bulunamadı";
    case "error":
      return row.error || "Hata";
    default:
      return row.status;
  }
}

function statusBadgeClass(status: ExamImportRow["status"]): string {
  switch (status) {
    case "new":
      return "badge-ok";
    case "existing":
    case "updated_only":
      return "badge-warn";
    case "not_found":
      return "badge-info";
    default:
      return "badge-err";
  }
}

export default function ExamEntryPdfImportModal({ onClose, onImported }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ExamImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<"idle" | "validating" | "importing">("idle");

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] ?? null;
    setSummary(null);
    setError(null);
    if (!next) {
      setFile(null);
      return;
    }
    if (!next.name.toLowerCase().endsWith(".pdf")) {
      setFile(null);
      setError("Sadece .pdf dosyaları kabul edilir.");
      return;
    }
    if (next.size === 0) {
      setFile(null);
      setError("Dosya boş.");
      return;
    }
    if (next.size > MAX_BYTES) {
      setFile(null);
      setError("Dosya 15MB sınırını aşıyor.");
      return;
    }
    setFile(next);
  };

  const runImport = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setPhase(dryRun ? "validating" : "importing");
    try {
      const result = await importTheoreticalExamPdf(file, { dryRun });
      setSummary(result);
      if (!dryRun && result.matched_count > 0) {
        onImported(result);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "PDF içe aktarma başarısız.");
    } finally {
      setBusy(false);
      setPhase("idle");
    }
  };

  const messageReadyRows = useMemo(
    () => (summary ? summary.rows.filter((r) => r.message_ready) : []),
    [summary],
  );

  const canCommit =
    !!file &&
    !!summary &&
    summary.dry_run &&
    summary.error_count === 0 &&
    summary.matched_count > 0;

  return (
    <div className="import-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="exam-pdf-import-title">
      <div className="import-modal import-modal--wide">
        <div className="import-modal__header">
          <h3 id="exam-pdf-import-title">Teorik E-Sınav Giriş Belgesi (PDF)</h3>
          <button type="button" className="import-modal__close" onClick={onClose} aria-label="Kapat">
            x
          </button>
        </div>

        <div className="import-modal__body">
          <section className="import-modal__step">
            <h4>1. MEBBİS PDF yükleyin</h4>
            <p className="import-modal__hint">
              MEBBİS&apos;ten aldığınız e-sınav giriş belgesi PDF&apos;ini yükleyin. Aynı ay (dönem) içinde
              daha önce işlenen adaylar tekrar mesaj kuyruğuna alınmaz; yalnızca listeye yeni eklenenler için
              WhatsApp mesajı hazırlanır. Tüm eşleşen adayların sınav tarihi ve salon bilgisi güncellenir.
            </p>
            <input type="file" accept=".pdf,application/pdf" onChange={onFileChange} disabled={busy} />
            {file && (
              <div className="import-modal__file-info">
                Seçilen: {file.name} ({Math.round(file.size / 1024)} KB)
              </div>
            )}
          </section>

          <section className="import-modal__step">
            <h4>2. Önizleme ve kaydet</h4>
            <div className="import-modal__actions-row">
              <button
                type="button"
                className="import-modal__btn-secondary"
                onClick={() => runImport(true)}
                disabled={!file || busy}
              >
                {phase === "validating" ? "Kontrol ediliyor..." : "Önizleme (kuru çalıştır)"}
              </button>
              <button
                type="button"
                className="import-modal__btn-primary"
                onClick={() => runImport(false)}
                disabled={!canCommit || busy}
                title={canCommit ? "" : "Önce önizleme yapın"}
              >
                {phase === "importing" ? UI_LOADING : "Kaydet ve güncelle"}
              </button>
            </div>
          </section>

          {error && <div className="import-modal__error">{error}</div>}

          {summary && (
            <section className="import-modal__summary">
              <div className="import-modal__summary-row">
                <span className="badge badge-info">Dönem: {summary.period_key}</span>
                <span className="badge badge-info">PDF: {summary.total_parsed} aday</span>
                <span className="badge badge-ok">Yeni: {summary.new_count}</span>
                <span className="badge badge-warn">Mevcut: {summary.existing_count}</span>
                <span className="badge badge-info">Bulunamadı: {summary.not_found_count}</span>
                {summary.error_count > 0 && (
                  <span className="badge badge-err">Hata: {summary.error_count}</span>
                )}
                {summary.dry_run ? (
                  <span className="badge badge-warn">Önizleme</span>
                ) : (
                  <span className="badge badge-ok">Kaydedildi</span>
                )}
              </div>

              {summary.dry_run && summary.new_count > 0 && (
                <div className="import-modal__success-hint">
                  {summary.new_count} yeni aday için WhatsApp mesajı hazır. Kaydettikten sonra tablodaki WA
                  düğmeleriyle gönderebilirsiniz.
                </div>
              )}

              {summary.dry_run && summary.new_count === 0 && summary.matched_count > 0 && (
                <div className="import-modal__success-hint">
                  Bu PDF&apos;deki tüm eşleşen adaylar bu dönemde daha önce işlenmiş. Sınav bilgileri yine de
                  güncellenecek; yeni mesaj kuyruğu oluşmayacak.
                </div>
              )}

              {!summary.dry_run && summary.matched_count > 0 && (
                <div className="import-modal__success-hint">
                  {summary.matched_count} adayın sınav bilgisi güncellendi.
                  {messageReadyRows.length > 0 &&
                    ` ${messageReadyRows.length} yeni aday için WhatsApp mesajı gönderebilirsiniz.`}
                </div>
              )}

              <div className="import-modal__errors-table">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>TC</th>
                      <th>Ad Soyad</th>
                      <th>Sınav</th>
                      <th>Durum</th>
                      <th>WA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.rows.map((row) => {
                      const wa =
                        !summary.dry_run && row.message_ready && row.phone
                          ? buildWhatsAppUrl(row.phone, buildTheoreticalExamWhatsAppMessage(row))
                          : null;
                      return (
                        <tr key={`${row.row_number}-${row.identity_no}`}>
                          <td>{row.row_number}</td>
                          <td className="muted">{row.identity_no}</td>
                          <td>{row.full_name_system || row.full_name_pdf}</td>
                          <td>
                            {formatDateTr(row.exam_date)} {row.exam_time.slice(0, 5)}
                            {row.exam_location && (
                              <>
                                <br />
                                <span className="muted">{row.exam_location}</span>
                              </>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${statusBadgeClass(row.status)}`}>
                              {statusLabel(row)}
                            </span>
                          </td>
                          <td>{wa ? <WhatsAppLink href={wa} /> : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>

        <div className="import-modal__footer">
          <button type="button" className="import-modal__btn-secondary" onClick={onClose} disabled={busy}>
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}
